import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

export type ThemePreference = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'themePreference';

interface ThemeContextType {
    theme: ThemePreference;
    isDark: boolean;
    setTheme: (theme: ThemePreference) => void;
    toggleDarkMode: () => void;
}

function getSystemPrefersDark(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function readStoredThemePreference(): ThemePreference {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'auto') {
            return stored;
        }

        const legacy = localStorage.getItem('darkMode');
        if (legacy === 'true') return 'dark';
        if (legacy === 'false') return 'light';
    } catch {
        // localStorage unavailable
    }
    return 'light';
}

export function resolveIsDark(theme: ThemePreference): boolean {
    if (theme === 'dark') return true;
    if (theme === 'light') return false;
    return getSystemPrefersDark();
}

const ThemeContext = createContext<ThemeContextType>({
    theme: 'light',
    isDark: false,
    setTheme: () => { },
    toggleDarkMode: () => { },
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<ThemePreference>(() => readStoredThemePreference());
    const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => getSystemPrefersDark());

    const isDark = useMemo(() => {
        if (theme === 'dark') return true;
        if (theme === 'light') return false;
        return systemPrefersDark;
    }, [theme, systemPrefersDark]);

    useEffect(() => {
        const root = document.documentElement;
        if (isDark) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [isDark]);

    useEffect(() => {
        if (theme !== 'auto') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event: MediaQueryListEvent) => {
            setSystemPrefersDark(event.matches);
        };

        setSystemPrefersDark(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    const setTheme = useCallback((next: ThemePreference) => {
        setThemeState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
            localStorage.removeItem('darkMode');
        } catch {
            // localStorage unavailable — silently ignore
        }
    }, []);

    const toggleDarkMode = useCallback(() => {
        setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light');
    }, [theme, setTheme]);

    return (
        <ThemeContext.Provider value={{ theme, isDark, setTheme, toggleDarkMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
